import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';

// Header Component
function Header() {
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 border-0 ${
        scrolled ? 'bg-white/95 backdrop-blur-md shadow-sm py-3 md:py-4' : 'bg-transparent py-3 md:py-6'
      }`}
    >
      <div className="w-full px-5 md:px-6 lg:px-16 flex items-center justify-between">
        <Link to="/" className="cursor-pointer">
          <img
            src="https://static.readdy.ai/image/45f36db358624bab4161a57e418814dc/952de5dd7ee479a30999c64f6b1ab29c.png"
            alt="GO TALENTS"
            className="h-9 md:h-14 lg:h-16 w-auto object-contain transition-all"
          />
        </Link>

        <a
          href="#apply-form"
          className="px-4 md:px-6 py-2 md:py-2.5 text-[11px] md:text-xs font-semibold tracking-wider rounded-full transition-all cursor-pointer whitespace-nowrap bg-[#F49729] text-white hover:bg-[#e08820]"
        >
          <span className="inline md:hidden">APPLY NOW</span>
          <span className="hidden md:inline">APPLY NOW</span>
        </a>
      </div>
    </header>
  );
}

// Hero Section
function HeroSection() {
  return (
    <section className="pt-24 md:pt-32 pb-0 px-5 md:px-6 lg:px-16 bg-[#1a1a1a] relative">
      <div className="absolute top-20 right-20 w-96 h-96 bg-[#F49729]/10 rounded-full blur-3xl"></div>
      <div className="absolute bottom-10 left-10 w-64 h-64 bg-[#F49729]/5 rounded-full blur-3xl"></div>

      <div className="max-w-6xl mx-auto text-center relative z-10 pb-16 md:pb-24">
        <p className="text-[#F49729] text-[10px] md:text-xs font-semibold tracking-[0.25em] uppercase mb-3 md:mb-4">
          Join Our Talent Network
        </p>
        <h1 className="text-[2rem] leading-[1.2] md:text-4xl lg:text-5xl xl:text-6xl font-bold text-white mb-4 md:mb-6">
          Launch Your
          <br />
          <span className="text-[#F49729]">Accounting Career</span>
        </h1>
      </div>

      {/* Curved bottom — sin gap en móvil */}
      <svg
        viewBox="0 0 1440 70"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
        className="w-full block"
        style={{ display: 'block', marginBottom: '-6px' }}
        preserveAspectRatio="none"
      >
        <path d="M-10 70 C400 10 1040 10 1450 70 L1450 75 L-10 75 Z" fill="white" />
      </svg>
    </section>
  );
}

// Benefits Section
const benefits = [
  {
    icon: 'ri-earth-line',
    title: 'Exclusive Opportunities',
    description: 'Access to job openings that are not available to the general public or listed on traditional job search websites.',
  },
  {
    icon: 'ri-money-dollar-circle-line',
    title: 'USD Compensation',
    description: 'Earn international-level salaries while enjoying the benefits of your local cost of living.',
  },
  {
    icon: 'ri-line-chart-line',
    title: 'Career Growth',
    description: 'Develop your skills working with diverse teams and cutting-edge technologies.',
  },
  {
    icon: 'ri-heart-pulse-line',
    title: 'Work-Life Balance',
    description: 'Enjoy flexibility - excel professionally without compromising your well-being.',
  },
  {
    icon: 'ri-customer-service-2-line',
    title: 'Dedicated Support',
    description: 'Our team guides you through the entire process and supports your professional development.',
  },
  {
    icon: 'ri-eye-line',
    title: 'Greater Visibility',
    description: 'Your profile is professionally presented to hiring companies, boosting your exposure and giving you a competitive edge.',
  },
];

function BenefitsSection() {
  return (
    <section className="py-14 md:py-24 px-5 md:px-6 lg:px-16 bg-white">
      <div className="max-w-6xl mx-auto">
        <div className="text-center mb-10 md:mb-16">
          <p className="text-[#F49729] text-[10px] md:text-xs font-semibold tracking-[0.25em] uppercase mb-2 md:mb-4">
            Why Join Us
          </p>
          <h2 className="text-2xl md:text-3xl lg:text-4xl font-bold text-[#111111] leading-tight">
            Benefits of Being a <span className="text-[#F49729]">TALENTER</span>
          </h2>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-8">
          {benefits.map((benefit, index) => (
            <div
              key={index}
              className="group flex items-start gap-4 md:flex-col md:items-start p-5 md:p-8 rounded-2xl bg-[#faf9f7] hover:bg-white hover:shadow-xl hover:shadow-[#F49729]/10 border border-transparent hover:border-[#F49729]/20 transition-all duration-300"
            >
              <div className="w-11 h-11 flex-shrink-0 md:w-14 md:h-14 flex items-center justify-center rounded-xl bg-white group-hover:bg-gradient-to-br group-hover:from-[#F49729] group-hover:to-[#e08820] text-[#F49729] group-hover:text-white md:mb-5 transition-all duration-300 shadow-sm">
                <i className={`${benefit.icon} text-xl md:text-2xl`}></i>
              </div>
              <div className="flex-1">
                <h3 className="text-sm md:text-lg font-semibold text-[#111111] mb-1 md:mb-3">{benefit.title}</h3>
                <p className="text-xs md:text-sm text-[#61616A] leading-relaxed">{benefit.description}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

// How It Works Section
const steps = [
  {
    number: '01',
    icon: 'ri-file-text-line',
    title: 'Apply',
    description: 'Fill out our application form with your professional information and experience.',
    color: 'from-[#F49729] to-[#e08820]',
  },
  {
    number: '02',
    icon: 'ri-search-eye-line',
    title: 'Get Evaluated',
    description: 'Our team reviews your profile and assesses your skills through our vetting process.',
    color: 'from-[#F49729] to-[#e08820]',
  },
  {
    number: '03',
    icon: 'ri-links-line',
    title: 'Match',
    description: 'We connect you with companies that match your expertise and career goals.',
    color: 'from-[#F49729] to-[#e08820]',
  },
  {
    number: '04',
    icon: 'ri-rocket-2-line',
    title: 'Start Working',
    description: 'Begin your new role with full support from our team throughout your journey.',
    color: 'from-[#F49729] to-[#e08820]',
  },
];

function HowItWorks() {
  return (
    <section className="py-14 md:py-24 px-5 md:px-6 lg:px-16 bg-[#faf9f7] relative overflow-hidden">
      <div className="absolute -top-40 -right-40 w-96 h-96 bg-[#F49729]/5 rounded-full blur-3xl"></div>
      <div className="absolute -bottom-20 -left-20 w-72 h-72 bg-[#F49729]/5 rounded-full blur-3xl"></div>

      <div className="max-w-6xl mx-auto relative z-10">
        <div className="text-center mb-10 md:mb-20">
          <p className="text-[#F49729] text-[10px] md:text-xs font-semibold tracking-[0.25em] uppercase mb-2 md:mb-4">
            The Process
          </p>
          <h2 className="text-2xl md:text-3xl lg:text-4xl font-bold text-[#111111] leading-tight">How It Works</h2>
          <p className="text-sm text-[#61616A] mt-2 md:mt-4 max-w-xl mx-auto">
            Four simple steps to launch your global career with GO Talents.
          </p>
        </div>

        {/* Desktop layout */}
        <div className="hidden md:block relative">
          <div className="absolute top-12 left-[12.5%] right-[12.5%] h-0.5 bg-gradient-to-r from-[#F49729]/20 via-[#F49729]/60 to-[#F49729]/20 z-0"></div>

          <div className="grid md:grid-cols-4 gap-6 relative z-10">
            {steps.map((step, index) => (
              <div key={index} className="flex flex-col items-center text-center group">
                <div className="relative mb-6">
                  <div className={`w-24 h-24 flex items-center justify-center rounded-full bg-gradient-to-br ${step.color} shadow-lg shadow-[#F49729]/30 group-hover:scale-110 transition-transform duration-300`}>
                    <i className={`${step.icon} text-3xl text-white`}></i>
                  </div>
                  <span className="absolute -top-1 -right-1 w-7 h-7 flex items-center justify-center rounded-full bg-white border-2 border-[#F49729] text-[#F49729] text-xs font-bold shadow-sm">
                    {index + 1}
                  </span>
                </div>

                <div className="bg-white rounded-2xl p-6 w-full shadow-sm border border-[#F49729]/10 group-hover:shadow-md group-hover:border-[#F49729]/30 transition-all duration-300">
                  <h3 className="text-base font-bold text-[#111111] mb-2">{step.title}</h3>
                  <p className="text-sm text-[#61616A] leading-relaxed">{step.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Mobile layout */}
        <div className="md:hidden flex flex-col">
          {steps.map((step, index) => (
            <div key={index} className="flex gap-5">
              {/* Left: icon + vertical line */}
              <div className="flex flex-col items-center flex-shrink-0">
                <div className={`w-12 h-12 flex items-center justify-center rounded-full bg-gradient-to-br ${step.color} shadow-md shadow-[#F49729]/20 relative`}>
                  <i className={`${step.icon} text-lg text-white`}></i>
                  <span className="absolute -top-1 -right-1 w-5 h-5 flex items-center justify-center rounded-full bg-white border-2 border-[#F49729] text-[#F49729] text-[9px] font-bold shadow-sm">
                    {index + 1}
                  </span>
                </div>
                {index < steps.length - 1 && (
                  <div className="w-0.5 flex-1 min-h-[2rem] bg-gradient-to-b from-[#F49729]/50 to-[#F49729]/10 my-2"></div>
                )}
              </div>

              {/* Right: content */}
              <div className={`flex-1 pb-7 ${index === steps.length - 1 ? 'pb-0' : ''}`}>
                <div className="bg-white rounded-2xl p-4 shadow-sm border border-[#F49729]/10">
                  <h3 className="text-sm font-bold text-[#111111] mb-1.5">{step.title}</h3>
                  <p className="text-xs text-[#61616A] leading-relaxed">{step.description}</p>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

// Industries Section
const industries = [
  { icon: 'ri-book-2-line', name: 'Bookkeeping' },
  { icon: 'ri-bank-card-line', name: 'Payroll Processing' },
  { icon: 'ri-file-text-line', name: 'Tax Preparation' },
  { icon: 'ri-bar-chart-box-line', name: 'Financial Reporting' },
  { icon: 'ri-shield-check-line', name: 'Audit Support' },
  { icon: 'ri-line-chart-line', name: 'Client Accounting Services' },
];

function Industries() {
  return (
    <section className="py-14 md:py-24 px-5 md:px-6 lg:px-16 bg-white">
      <div className="max-w-6xl mx-auto">
        <div className="text-center mb-8 md:mb-16">
          <p className="text-[#F49729] text-[10px] md:text-xs font-semibold tracking-[0.25em] uppercase mb-2 md:mb-4">Areas We Hire</p>
          <h2 className="text-2xl md:text-3xl lg:text-4xl font-bold text-[#111111] leading-tight">Find Your Field</h2>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3 md:gap-6">
          {industries.map((industry, index) => (
            <div
              key={index}
              className="group p-4 md:p-6 rounded-2xl bg-[#faf9f7] hover:bg-gradient-to-br hover:from-[#F49729] hover:to-[#e08820] transition-all duration-300 text-center cursor-default"
            >
              <div className="w-10 h-10 md:w-14 md:h-14 flex items-center justify-center rounded-xl bg-white text-[#F49729] mx-auto mb-3 md:mb-4 group-hover:bg-white/20 group-hover:text-white transition-all duration-300">
                <i className={`${industry.icon} text-lg md:text-2xl`}></i>
              </div>
              <p className="text-[11px] md:text-sm font-medium text-[#111111] group-hover:text-white transition-all duration-300 leading-snug">
                {industry.name}
              </p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

// Application Form Section
function ApplicationForm() {
  const [formData, setFormData] = useState({
    fullName: '',
    email: '',
    phone: '',
    country: '',
    linkedIn: '',
    expertise: '',
    experienceLevel: '',
    englishLevel: '',
    availability: '',
  });
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const formDataEncoded = new URLSearchParams();
      Object.entries(formData).forEach(([key, value]) => {
        formDataEncoded.append(key, value);
      });
formDataEncoded.append('form_type', 'apply');
      await fetch('/contact.php', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: formDataEncoded.toString(),
      });
      setSubmitted(true);
    } catch (err) {
      console.error('Form submission failed:', err);
    } finally {
      setSubmitting(false);
    }
  };

  const selectStyle = {
    backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 24 24' stroke='white'%3E%3Cpath stroke-linecap='round' stroke-linejoin='round' stroke-width='2' d='M19 9l-7 7-7-7'%3E%3C/path%3E%3C/svg%3E")`,
    backgroundRepeat: 'no-repeat',
    backgroundPosition: 'right 1rem center',
    backgroundSize: '1.25rem',
  };

  return (
    <section id="apply-form" className="py-14 md:py-24 px-5 md:px-6 lg:px-16 bg-[#1a1a1a] relative overflow-hidden">
      <div className="absolute top-0 left-0 right-0 z-10">
        <svg viewBox="0 0 1440 80" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-full h-auto">
          <path d="M0 0L0 30C360 60 720 50 1080 40C1260 30 1380 20 1440 30L1440 0L0 0Z" fill="white" />
        </svg>
      </div>

      <div className="absolute top-20 right-20 w-80 h-80 bg-[#F49729]/10 rounded-full blur-3xl"></div>
      <div className="absolute bottom-20 left-20 w-64 h-64 bg-[#F49729]/5 rounded-full blur-3xl"></div>

      <div className="max-w-4xl mx-auto relative z-10 pt-8 md:pt-10">
        <div className="text-center mb-8 md:mb-12">
          <p className="text-[#F49729] text-[10px] md:text-xs font-semibold tracking-[0.25em] uppercase mb-3 md:mb-6">
            Ready to Start?
          </p>
          <h2 className="text-[1.75rem] leading-tight md:text-4xl lg:text-5xl xl:text-6xl font-bold text-white mb-3 md:mb-6">
            Join Our
            <br />
            <span className="text-[#F49729]">Talent Network</span>
          </h2>
          <p className="text-sm md:text-lg text-white/60 max-w-2xl mx-auto leading-relaxed">
            Applications are reviewed within 1 week — placement timing after that depends on current company demand.
          </p>
        </div>

        {submitted ? (
          <div className="bg-white/10 backdrop-blur-sm rounded-3xl p-8 md:p-12 text-center">
            <div className="w-16 h-16 md:w-20 md:h-20 flex items-center justify-center rounded-full bg-[#F49729] text-white mx-auto mb-5 md:mb-6">
              <i className="ri-check-line text-3xl md:text-4xl"></i>
            </div>
            <h3 className="text-xl md:text-2xl font-bold text-white mb-2 md:mb-3">Application Received!</h3>
            <p className="text-sm md:text-base text-white/60">
              Thank you for applying. Our team will review your profile and contact you soon.
            </p>
          </div>
        ) : (
          <form
            id="talent-application-form"
            data-readdy-form
            onSubmit={handleSubmit}
            className="bg-white/10 backdrop-blur-sm rounded-3xl p-5 md:p-8 lg:p-12"
          >
            {/* Row 1 */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6 mb-4 md:mb-6">
              <div>
                <label htmlFor="fullName" className="block text-xs md:text-sm font-medium text-white mb-1.5 md:mb-2">
                  Full Name *
                </label>
                <input
                  type="text"
                  id="fullName"
                  name="fullName"
                  value={formData.fullName}
                  onChange={handleChange}
                  required
                  placeholder="John Smith"
                  className="w-full px-4 py-3 md:px-5 md:py-4 text-sm bg-white/10 border border-white/20 rounded-xl text-white placeholder-white/40 focus:outline-none focus:ring-2 focus:ring-[#F49729]/50 focus:border-[#F49729] transition-all"
                />
              </div>
              <div>
                <label htmlFor="email" className="block text-xs md:text-sm font-medium text-white mb-1.5 md:mb-2">
                  Email *
                </label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  placeholder="john@email.com"
                  className="w-full px-4 py-3 md:px-5 md:py-4 text-sm bg-white/10 border border-white/20 rounded-xl text-white placeholder-white/40 focus:outline-none focus:ring-2 focus:ring-[#F49729]/50 focus:border-[#F49729] transition-all"
                />
              </div>
            </div>

            {/* Row 2 */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6 mb-4 md:mb-6">
              <div>
                <label htmlFor="phone" className="block text-xs md:text-sm font-medium text-white mb-1.5 md:mb-2">
                  Phone Number *
                </label>
                <input
                  type="tel"
                  id="phone"
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                  required
                  placeholder="+54 123 1231231"
                  className="w-full px-4 py-3 md:px-5 md:py-4 text-sm bg-white/10 border border-white/20 rounded-xl text-white placeholder-white/40 focus:outline-none focus:ring-2 focus:ring-[#F49729]/50 focus:border-[#F49729] transition-all"
                />
              </div>
              <div>
                <label htmlFor="country" className="block text-xs md:text-sm font-medium text-white mb-1.5 md:mb-2">
                  Country *
                </label>
                <input
                  type="text"
                  id="country"
                  name="country"
                  value={formData.country}
                  onChange={handleChange}
                  required
                  placeholder="Argentina"
                  className="w-full px-4 py-3 md:px-5 md:py-4 text-sm bg-white/10 border border-white/20 rounded-xl text-white placeholder-white/40 focus:outline-none focus:ring-2 focus:ring-[#F49729]/50 focus:border-[#F49729] transition-all"
                />
              </div>
            </div>

            {/* LinkedIn */}
            <div className="mb-4 md:mb-6">
              <label htmlFor="linkedIn" className="block text-xs md:text-sm font-medium text-white mb-1.5 md:mb-2">
                LinkedIn Profile (optional)
              </label>
              <input
                type="url"
                id="linkedIn"
                name="linkedIn"
                value={formData.linkedIn}
                onChange={handleChange}
                placeholder="https://linkedin.com/in/yourprofile"
                className="w-full px-4 py-3 md:px-5 md:py-4 text-sm bg-white/10 border border-white/20 rounded-xl text-white placeholder-white/40 focus:outline-none focus:ring-2 focus:ring-[#F49729]/50 focus:border-[#F49729] transition-all"
              />
            </div>

            {/* Row 3 */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6 mb-4 md:mb-6">
              <div>
                <label htmlFor="expertise" className="block text-xs md:text-sm font-medium text-white mb-1.5 md:mb-2">
                  Area of Expertise *
                </label>
                <select
                  id="expertise"
                  name="expertise"
                  value={formData.expertise}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 md:px-5 md:py-4 text-sm bg-white/10 border border-white/20 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-[#F49729]/50 focus:border-[#F49729] transition-all cursor-pointer appearance-none"
                  style={selectStyle}
                >
                  <option value="" className="bg-[#1a1a1a] text-white">Select area</option>
                  <option value="Accounting" className="bg-[#1a1a1a] text-white">Accounting</option>
                  <option value="Bookkeeping" className="bg-[#1a1a1a] text-white">Bookkeeping</option>
                  <option value="Other" className="bg-[#1a1a1a] text-white">Other</option>
                </select>
              </div>
              <div>
                <label htmlFor="experienceLevel" className="block text-xs md:text-sm font-medium text-white mb-1.5 md:mb-2">
                  Experience Level *
                </label>
                <select
                  id="experienceLevel"
                  name="experienceLevel"
                  value={formData.experienceLevel}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 md:px-5 md:py-4 text-sm bg-white/10 border border-white/20 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-[#F49729]/50 focus:border-[#F49729] transition-all cursor-pointer appearance-none"
                  style={selectStyle}
                >
                  <option value="" className="bg-[#1a1a1a] text-white">Select level</option>
                  <option value="Junior" className="bg-[#1a1a1a] text-white">Junior (0-2 years)</option>
                  <option value="Semi-Senior" className="bg-[#1a1a1a] text-white">Semi-Senior (2-5 years)</option>
                  <option value="Senior" className="bg-[#1a1a1a] text-white">Senior (5+ years)</option>
                </select>
              </div>
            </div>

            {/* Row 4 */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6 mb-6 md:mb-6">
              <div>
                <label htmlFor="englishLevel" className="block text-xs md:text-sm font-medium text-white mb-1.5 md:mb-2">
                  English Level *
                </label>
                <select
                  id="englishLevel"
                  name="englishLevel"
                  value={formData.englishLevel}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 md:px-5 md:py-4 text-sm bg-white/10 border border-white/20 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-[#F49729]/50 focus:border-[#F49729] transition-all cursor-pointer appearance-none"
                  style={selectStyle}
                >
                  <option value="" className="bg-[#1a1a1a] text-white">Select level</option>
                  <option value="Basic" className="bg-[#1a1a1a] text-white">Basic</option>
                  <option value="Intermediate" className="bg-[#1a1a1a] text-white">Intermediate</option>
                  <option value="Advanced" className="bg-[#1a1a1a] text-white">Advanced</option>
                  <option value="Native/Bilingual" className="bg-[#1a1a1a] text-white">Native/Bilingual</option>
                </select>
              </div>
              <div>
                <label htmlFor="availability" className="block text-xs md:text-sm font-medium text-white mb-1.5 md:mb-2">
                  Availability *
                </label>
                <select
                  id="availability"
                  name="availability"
                  value={formData.availability}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 md:px-5 md:py-4 text-sm bg-white/10 border border-white/20 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-[#F49729]/50 focus:border-[#F49729] transition-all cursor-pointer appearance-none"
                  style={selectStyle}
                >
                  <option value="" className="bg-[#1a1a1a] text-white">Select availability</option>
                  <option value="Immediate" className="bg-[#1a1a1a] text-white">Immediate</option>
                  <option value="2 weeks" className="bg-[#1a1a1a] text-white">2 weeks</option>
                  <option value="1 month" className="bg-[#1a1a1a] text-white">1 month</option>
                  <option value="More than 1 month" className="bg-[#1a1a1a] text-white">More than 1 month</option>
                </select>
              </div>
            </div>

            <button
              type="submit"
              disabled={submitting}
              className="w-full py-4 text-sm font-semibold tracking-wider text-white bg-[#F49729] rounded-xl hover:bg-[#e08820] transition-all cursor-pointer whitespace-nowrap disabled:opacity-60 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            >
              {submitting ? (
                <>
                  <i className="ri-loader-4-line animate-spin"></i>
                  SUBMITTING...
                </>
              ) : (
                <>
                  SUBMIT APPLICATION
                  <i className="ri-arrow-right-line"></i>
                </>
              )}
            </button>
          </form>
        )}
      </div>
    </section>
  );
}

// Footer Component
function Footer() {
  const [email, setEmail] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!email) return;
    setSubmitting(true);
    try {
      const formData = new URLSearchParams();
      formData.append('email', email);
      formData.append('form_type', 'newsletter');
      await fetch('/contact.php', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: formData.toString(),
      });
      setSubmitted(true);
      setEmail('');
    } catch (err) {
      console.error('Newsletter subscription failed:', err);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <footer className="relative overflow-hidden">
      <div className="absolute top-0 left-0 right-0 z-10">
        <svg viewBox="0 0 1440 80" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-full h-auto">
          <path d="M0 0L0 40C360 80 720 60 1080 40C1260 30 1380 20 1440 30L1440 0L0 0Z" fill="#1a1a1a" />
        </svg>
      </div>

      <div className="bg-[#f5f0eb] pt-16 md:pt-24 pb-6 md:pb-8 px-5 md:px-6 lg:px-16">
        <div className="max-w-6xl mx-auto relative z-10">
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-8 md:gap-10 mb-10 md:mb-14">
            {/* Logo — full width on mobile */}
            <div className="col-span-2 md:col-span-1">
              <Link to="/">
                <img
                  src="https://static.readdy.ai/image/45f36db358624bab4161a57e418814dc/952de5dd7ee479a30999c64f6b1ab29c.png"
                  alt="GO TALENTS"
                  className="h-12 md:h-20 w-auto object-contain mb-2 cursor-pointer"
                />
              </Link>
            </div>

            {/* Quick Links */}
            <div>
              <h4 className="text-xs font-semibold text-[#111111] uppercase tracking-wider mb-4 md:mb-5">
                <a href="/">Quick Links</a>
              </h4>
              <div className="space-y-3">
                <Link to="/hire-talent" className="block text-sm text-[#61616A] hover:text-[#F49729] transition-colors cursor-pointer">
                  Hire Talent
                </Link>
                <Link to="/apply-talent" className="block text-sm text-[#61616A] hover:text-[#F49729] transition-colors cursor-pointer">
                  Apply as Talent
                </Link>
                <Link to="/privacy" className="block text-sm text-[#61616A] hover:text-[#F49729] transition-colors cursor-pointer">
                  Privacy Policy
                </Link>
              </div>
            </div>

            {/* Contact */}
            <div>
              <h4 className="text-xs font-semibold text-[#111111] uppercase tracking-wider mb-4 md:mb-5">
                <a href="mailto:contact@gotalentsglobal.com">Contact</a>
              </h4>
              <a
                href="mailto:contact@gotalentsglobal.com"
                className="block text-sm text-[#61616A] hover:text-[#F49729] transition-colors mb-4 cursor-pointer break-all"
              >
                contact@<wbr />gotalentsglobal.com
              </a>
              <div className="flex items-center gap-3">
                <a
                  href="https://www.linkedin.com/company/go-talentsglobal"
                  rel="nofollow"
                  className="w-9 h-9 flex items-center justify-center rounded-full bg-white text-[#61616A] hover:text-[#F49729] hover:shadow-lg transition-all cursor-pointer"
                >
                  <i className="ri-linkedin-fill text-base"></i>
                </a>
              </div>
            </div>

            {/* Newsletter — full width on mobile */}
            <div className="col-span-2 md:col-span-1">
              <h4 className="text-xs font-semibold text-[#111111] uppercase tracking-wider mb-3 md:mb-5">Newsletter</h4>
              <p className="text-sm text-[#61616A] mb-3 md:mb-4">
                Stay updated with the latest opportunities and insights.
              </p>
              {submitted ? (
                <div className="bg-[#F49729]/10 rounded-2xl p-4">
                  <p className="text-sm text-[#F49729] font-medium">Thank you for subscribing!</p>
                </div>
              ) : (
                <form id="newsletter-form-apply" data-readdy-form onSubmit={handleSubmit} className="flex gap-2">
                  <input
                    type="email"
                    name="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="Your email"
                    required
                    className="flex-1 min-w-0 px-4 py-3 text-sm bg-white border-0 rounded-full focus:outline-none focus:ring-2 focus:ring-[#F49729]/30 transition-all"
                  />
                  <button
                    type="submit"
                    disabled={submitting}
                    className="px-4 py-3 bg-[#F49729] text-white rounded-full hover:bg-[#e08820] transition-colors cursor-pointer whitespace-nowrap disabled:opacity-60 flex items-center justify-center"
                  >
                    <i className="ri-arrow-right-line text-sm"></i>
                  </button>
                </form>
              )}
            </div>
          </div>

          <div className="pt-5 md:pt-8 border-t border-[#d9d0c5] flex flex-col md:flex-row items-center justify-between gap-3">
            <p className="text-xs text-[#7F7F84]">&copy; 2026 GO TALENTS.</p>
          </div>
        </div>
      </div>
    </footer>
  );
}

// Main Page Component
export default function ApplyTalent() {
  return (
    <div className="min-h-screen bg-white font-['Inter',system-ui,sans-serif]">
      <Header />
      <main>
        <HeroSection />
        <BenefitsSection />
        <HowItWorks />
        <Industries />
        <ApplicationForm />
      </main>
      <Footer />
    </div>
  );
}
